import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
  type Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { merge, of, Subject, type Observable } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, share, switchMap, tap } from 'rxjs/operators';

import { hasClientRole, isInternalRole } from '../session/session.model';
import { SessionService } from '../session/session.service';
import { ClockService } from '../time/clock.service';
import {
  isValidDateRangeSelection,
  resolveDateRange,
  utcDayOf,
  utcDayStartMs,
} from '../time/utc-date-range';
import {
  DEFAULT_FILTER_STATE,
  dateRangeSelectionEquals,
  filterScopeEquals,
  filterStateEquals,
  STATUS_VALUES,
  type DateRangeSelection,
  type FilterDimension,
  type FilterScope,
  type FilterState,
  type ResolvedDateRange,
  type StatusValue,
} from './filter-state.model';
import { FilterTelemetry } from './filter-telemetry';

/**
 * The coalescing window (FR-010, SC-006). The spec's stated ~300 ms: five
 * changes forming one burst produce exactly one refresh per subscribed view.
 */
export const FILTER_DEBOUNCE_MS = 300;

/**
 * The port every dashboard view codes against
 * (contracts/filter-state.contract.md). Deliberately narrow: consumers read
 * `scope()` and re-fetch, they never write it — the bar owns every mutation.
 *
 * There is no `setX()` — and, since the 2026-08-26 contract revision, no
 * `reset()` — here on purpose: the port is genuinely read-only. The bar's own
 * controls, the FR-014 Reset included, use {@link FilterScopeController}, the
 * internal writable surface.
 */
@Injectable()
export abstract class FilterScopeService {
  /** The published, debounced scope. Read this in a computed/effect. */
  abstract readonly scope: Signal<FilterScope>;

  /** Resolved half-open UTC window for the current selection. Equals `scope().range`. */
  abstract readonly range: Signal<ResolvedDateRange>;

  /**
   * True while the bar shows a selection that {@link scope} has not published yet — for at most
   * the coalescing window ({@link FILTER_DEBOUNCE_MS}).
   *
   * GH-164 stage 6 (review of `aith-ui` #49). A view that only READS the scope never needs this: it
   * catches up when the publication lands. A view that SENDS the scope on a user action does. On
   * `/exports`, picking an application and pressing Request export inside the window sent the
   * previous filter — or, after a client change, exported the previous client — while the bar
   * already showed the new one. Such a view holds its action until this is false, rather than
   * reading the draft, so what it declares and what it sends stay the published scope.
   *
   * Not abstract: a stub with no draft layer is never pending.
   */
  readonly pending: Signal<boolean> = signal(false).asReadonly();
}

/**
 * What caused a publication — carried through the pipeline for telemetry only.
 * `session` is the FR-019 boundary clear and `identity` the arrival of the
 * first owner: both immediate like `reset`, but neither is a user change, so
 * neither records telemetry.
 */
type PublicationCause = 'change' | 'reset' | 'restore' | 'session' | 'identity';

interface Publication {
  readonly scope: FilterScope;
  /**
   * The *selection* behind {@link scope}. Carried because a publication is
   * deduplicated and diffed on selection identity, not on the resolved window:
   * two different selections can resolve to the same window, and the URL and
   * the telemetry both describe the selection (data-model §1).
   */
  readonly state: FilterState;
  readonly cause: PublicationCause;
}

/**
 * The root-provided implementation and the bar's internal writable surface.
 *
 * Two layers (R-009):
 *
 * - **Draft** — per-dimension signals that update *synchronously* on
 *   interaction, so a control never lags its own input (SC-001). Read by the
 *   bar's controls only.
 * - **Published** — one composed {@link FilterScope}, derived by bridging the
 *   composed draft through `toObservable → switchMap(delay(300)) → toSignal`
 *   (D6/Art. XI-C: RxJS confined to the genuinely-async case, bridged at the
 *   boundary). Read by every subscribed view, by the URL writer, and by
 *   observability.
 *
 * `switchMap(delay(…))` rather than `debounceTime(…)` is what makes
 * {@link reset} both immediate **and** cancelling: an immediate publication
 * arrives on the same stream and switchMap discards the pending delayed one, so
 * a change followed by a reset inside the window yields exactly one emission
 * (SC-004, obligation 4). {@link shouldPublish} is the second half of that
 * guarantee — it drops the trailing duplicate the reset's own draft writes
 * would otherwise debounce into.
 */
@Injectable()
export class FilterScopeController extends FilterScopeService {
  private readonly clock = inject(ClockService);
  private readonly session = inject(SessionService);
  private readonly telemetry = inject(FilterTelemetry);

  // ─── Draft layer (synchronous) ────────────────────────────────────────────

  private readonly dateRangeState = signal<DateRangeSelection>(DEFAULT_FILTER_STATE.dateRange);
  private readonly clientCodeState = signal<string | null>(DEFAULT_FILTER_STATE.clientCode);
  private readonly appState = signal<string | null>(DEFAULT_FILTER_STATE.app);
  private readonly modelState = signal<string | null>(DEFAULT_FILTER_STATE.model);
  private readonly providerState = signal<string | null>(DEFAULT_FILTER_STATE.provider);
  private readonly appUnspecifiedState = signal(DEFAULT_FILTER_STATE.appUnspecified);
  private readonly modelUnspecifiedState = signal(DEFAULT_FILTER_STATE.modelUnspecified);
  private readonly providerUnspecifiedState = signal(DEFAULT_FILTER_STATE.providerUnspecified);
  private readonly userState = signal<string | null>(DEFAULT_FILTER_STATE.user);
  private readonly statusState = signal<readonly StatusValue[]>(DEFAULT_FILTER_STATE.status);

  readonly draftDateRange = this.dateRangeState.asReadonly();
  readonly draftClientCode = this.clientCodeState.asReadonly();
  readonly draftApp = this.appState.asReadonly();
  readonly draftModel = this.modelState.asReadonly();
  readonly draftProvider = this.providerState.asReadonly();
  readonly draftAppUnspecified = this.appUnspecifiedState.asReadonly();
  readonly draftModelUnspecified = this.modelUnspecifiedState.asReadonly();
  readonly draftProviderUnspecified = this.providerUnspecifiedState.asReadonly();
  readonly draftUser = this.userState.asReadonly();
  readonly draftStatus = this.statusState.asReadonly();

  /**
   * Bumped by {@link reset} and the FR-019 session-boundary clear. The bar's
   * controls watch it to discard *local* drafts (the date picker's in-progress
   * Custom fields) that a defaults write alone cannot reach: writing the same
   * default reference into a signal notifies nobody.
   */
  private readonly resetRevisionState = signal(0);
  readonly resetRevision = this.resetRevisionState.asReadonly();

  /**
   * True for `InternalAdmin`/`InternalViewer`. The client selector renders only
   * for these roles (FR-004) — and hiding it is UX only: the API's `403` is the
   * enforcement (FR-005, SC-007).
   */
  readonly isInternalRole = computed(() => isInternalRole(this.session.session()));

  /**
   * FR-005's actual subject: the client-portal roles. Deliberately not the
   * negation of {@link isInternalRole} — the signed-out and pre-sign-in
   * sessions are neither internal nor client.
   */
  readonly isClientRole = computed(() => hasClientRole(this.session.session()));

  /**
   * Data-model §3 invariant 3: for client roles the scope is always the
   * session's identity-derived code, never a selection and never a URL value.
   */
  private readonly effectiveClientCode = computed(() =>
    this.isInternalRole() ? this.clientCodeState() : this.session.session().clientCode,
  );

  /**
   * R-018. Reading the UTC *day* rather than the raw instant is what makes this
   * fire once per rollover: the clock ticks every second but the derived day
   * string is unchanged, so nothing downstream is notified until midnight.
   */
  private readonly utcDay = computed(() => utcDayOf(this.clock.now()));

  private readonly resolvedRange = computed<ResolvedDateRange>(() => {
    const selection = this.dateRangeState();
    // Signals track dynamically: a custom selection never reads `utcDay`, so a
    // custom range is absolute and the clock can never re-resolve it (R-018).
    if (selection.kind === 'custom') return resolveDateRange(selection, 0);
    return resolveDateRange(selection, utcDayStartMs(this.utcDay()));
  });

  /**
   * The current draft selection as a whole — what the URL projects (FR-011).
   *
   * Deliberately the *selection*, not {@link scope}: the published scope
   * carries the resolved window, and `dateRange=today` and a custom range that
   * happens to cover today resolve identically but must not share a URL
   * (data-model §1).
   */
  readonly currentState = computed<FilterState>(() => ({
    dateRange: this.dateRangeState(),
    clientCode: this.effectiveClientCode(),
    app: this.appState(),
    model: this.modelState(),
    provider: this.providerState(),
    appUnspecified: this.appUnspecifiedState(),
    modelUnspecified: this.modelUnspecifiedState(),
    providerUnspecified: this.providerUnspecifiedState(),
    user: this.userState(),
    status: this.statusState(),
  }));

  private readonly composedScope = computed<FilterScope>(() => ({
    range: this.resolvedRange(),
    clientCode: this.effectiveClientCode(),
    app: this.appState(),
    model: this.modelState(),
    provider: this.providerState(),
    appUnspecified: this.appUnspecifiedState(),
    modelUnspecified: this.modelUnspecifiedState(),
    providerUnspecified: this.providerUnspecifiedState(),
    user: this.userState(),
    status: this.statusState(),
  }));

  // ─── Published layer (coalesced) ──────────────────────────────────────────

  /** Publications that bypass and cancel the window: reset and initialisation. */
  private readonly immediate = new Subject<PublicationCause>();

  private readonly publications: Observable<FilterScope>;

  readonly scope: Signal<FilterScope>;
  readonly range: Signal<ResolvedDateRange>;

  /** The draft differs from the published scope: a change is inside the coalescing window. */
  override readonly pending: Signal<boolean> = computed(
    () => !filterScopeEquals(this.composedScope(), this.scope()),
  );

  /** Snapshot of the last published *selection* — the dedupe and telemetry baseline. */
  private lastPublishedState: FilterState;
  /** …and of its resolved window, which can move on its own at UTC midnight (R-018). */
  private lastPublished: FilterScope;

  constructor() {
    super();
    this.lastPublishedState = this.currentState();
    this.lastPublished = this.composedScope();

    const drafted = toObservable(this.composedScope).pipe(
      map((scope): Publication => ({ scope, state: this.currentState(), cause: 'change' })),
    );
    const immediate = this.immediate.pipe(
      map((cause): Publication => ({
        scope: this.composedScope(),
        state: this.currentState(),
        cause,
      })),
    );

    this.publications = merge(drafted, immediate).pipe(
      switchMap((publication) =>
        publication.cause === 'change'
          ? of(publication).pipe(delay(FILTER_DEBOUNCE_MS))
          : of(publication),
      ),
      filter((publication) => this.shouldPublish(publication)),
      tap((publication) => this.recordTelemetry(publication)),
      map((publication) => publication.scope),
      share(),
    );

    // Consumers ask "would I fetch the same data?", so they dedupe on the
    // resolved window: a selection change that resolves to an equivalent
    // window rewrites the URL (above) without making every dashboard refetch.
    this.scope = toSignal(this.publications.pipe(distinctUntilChanged(filterScopeEquals)), {
      initialValue: this.composedScope(),
    });
    this.range = computed(() => this.scope().range);

    // FR-019/R-019: a change of session *owner* clears every draft — sign-out
    // keeps the SPA (and this root service) alive, and the next owner must
    // never inherit the previous one's selections, the identity-bearing
    // free-text `user` above all.
    //
    // The signal is the ownership epoch, not the falling edge of
    // authentication: an owner can be replaced *in place* (AITH-43 permits a
    // second sign-in with no sign-out first), and `isAuthenticated` is true on
    // both sides of that — an auth-edge rule would let the drafts cross. The
    // epoch is also what distinguishes a replacement from a renewal, which
    // replaces the Session object for the *same* owner and must clear nothing.
    //
    // `hadOwner` is what keeps boot → first sign-in from firing: there is no
    // previous owner to clear after, and the URL restore legitimately precedes
    // sign-in (FR-012), so clearing there would wipe a deep link.
    //
    // The FIRST owner is the other half of the same transition and needs the
    // opposite treatment: nothing to clear, but the scope genuinely moves.
    // `effectiveClientCode` is role-resolved, so a staff deep link's restored
    // `client` becomes effective only once an internal role authenticates —
    // and routing that through the 300 ms window treats identity resolution
    // like a keystroke. A view mounting inside it reads `clientCode: null`
    // (AITH-43 returns to the stored deep link immediately after sign-in, so it
    // usually does), and the bar itself shows the selected client beside three
    // selectors saying "select a client first". Publishing immediately closes
    // the window; the drafts are deliberately untouched, because the URL
    // restore legitimately precedes sign-in (FR-012).
    let lastEpoch = untracked(() => this.session.sessionEpoch());
    let hadOwner = untracked(() => this.session.isAuthenticated());
    effect(() => {
      const epoch = this.session.sessionEpoch();
      const authenticated = this.session.isAuthenticated();
      const epochMoved = epoch !== lastEpoch;
      const ownerChanged = epochMoved && hadOwner;
      const firstOwner = epochMoved && !hadOwner && authenticated;
      lastEpoch = epoch;
      hadOwner = authenticated;
      if (ownerChanged) untracked(() => this.clearForSessionEnd());
      else if (firstOwner) untracked(() => this.immediate.next('identity'));
    });

    inject(DestroyRef).onDestroy(() => this.immediate.complete());
  }

  /**
   * Every published change, in order — the internal counterpart of
   * {@link scope} for collaborators that must act *per publication* rather than
   * per value: the URL writer (FR-011) and this feature's own specs. Not on the
   * port; views read `scope()`.
   */
  get scopeChanges(): Observable<FilterScope> {
    return this.publications;
  }

  // ─── Writes (the bar's controls only) ─────────────────────────────────────

  /**
   * @returns `false` when the selection is invalid (an inverted or malformed
   * custom range). The previous valid selection stays active and nothing is
   * published — the control renders inline feedback (data-model §10).
   */
  setDateRange(selection: DateRangeSelection): boolean {
    if (!isValidDateRangeSelection(selection)) return false;
    this.dateRangeState.set(selection);
    return true;
  }

  /**
   * No-op for client roles: their scope is identity-derived and never writable
   * (FR-005). Guarded on *having a client role* rather than on not-internal:
   * a deep-link restore runs before sign-in (memory-only OIDC storage), and the
   * restored draft must survive into a staff session — it only ever becomes
   * effective through {@link effectiveClientCode}, which requires an
   * authenticated internal role.
   */
  setClientCode(clientCode: string | null): void {
    if (hasClientRole(this.session.session())) return;
    this.clientCodeState.set(clientCode);
  }

  setApp(value: string | null): void {
    this.appState.set(value);
    this.appUnspecifiedState.set(false);
  }

  setModel(value: string | null): void {
    this.modelState.set(value);
    this.modelUnspecifiedState.set(false);
  }

  setProvider(value: string | null): void {
    this.providerState.set(value);
    this.providerUnspecifiedState.set(false);
  }

  /**
   * GH-164 C-4: select the BLANK segment for an open-set dimension — events that carried no value.
   * Clears that dimension's value, because the two are one choice, never both.
   */
  selectUnspecified(dimension: 'app' | 'model' | 'provider'): void {
    switch (dimension) {
      case 'app':
        this.appState.set(null);
        this.appUnspecifiedState.set(true);
        return;
      case 'model':
        this.modelState.set(null);
        this.modelUnspecifiedState.set(true);
        return;
      case 'provider':
        this.providerState.set(null);
        this.providerUnspecifiedState.set(true);
        return;
    }
  }

  /**
   * The free-text user filter's commit (FR-008, R-014). Keystrokes never reach
   * here — the control holds its own local draft and calls this on Enter/blur
   * only. An empty or whitespace-only commit clears to `null`, never `''`
   * (data-model §3 invariant 4), and re-committing an unchanged value is a
   * no-op, so a Tab pass over the field publishes nothing.
   */
  commitUser(value: string | null): void {
    const trimmed = value?.trim() ?? '';
    this.userState.set(trimmed === '' ? null : trimmed);
  }

  /** Held de-duplicated and in canonical order, so the same selection always yields the same URL (R-006). */
  setStatus(values: readonly StatusValue[]): void {
    const selected = new Set(values);
    const canonical = STATUS_VALUES.filter((value) => selected.has(value));
    if (
      canonical.length === this.statusState().length
      && canonical.every((value, index) => value === this.statusState()[index])
    ) {
      return;
    }
    this.statusState.set(Object.freeze(canonical));
  }

  /** FR-014: one action, every dimension back to its default, published immediately. */
  reset(): void {
    this.writeState(DEFAULT_FILTER_STATE);
    this.resetRevisionState.update((revision) => revision + 1);
    this.immediate.next('reset');
  }

  /**
   * FR-019/R-019: the session-boundary clear. Writes the draft signals
   * directly — {@link setClientCode}'s role guard is deliberately bypassed,
   * because after a client-role sign-out the roles are gone but the retained
   * draft must still be wiped. Publishes immediately with the non-user
   * `session` cause: pending debounced changes are cancelled, no telemetry is
   * recorded, and {@link shouldPublish} drops it entirely when the published
   * scope is already the default.
   */
  private clearForSessionEnd(): void {
    this.dateRangeState.set(DEFAULT_FILTER_STATE.dateRange);
    this.clientCodeState.set(DEFAULT_FILTER_STATE.clientCode);
    this.appState.set(DEFAULT_FILTER_STATE.app);
    this.modelState.set(DEFAULT_FILTER_STATE.model);
    this.providerState.set(DEFAULT_FILTER_STATE.provider);
    this.appUnspecifiedState.set(DEFAULT_FILTER_STATE.appUnspecified);
    this.modelUnspecifiedState.set(DEFAULT_FILTER_STATE.modelUnspecified);
    this.providerUnspecifiedState.set(DEFAULT_FILTER_STATE.providerUnspecified);
    this.userState.set(DEFAULT_FILTER_STATE.user);
    this.statusState.set(DEFAULT_FILTER_STATE.status);
    this.resetRevisionState.update((revision) => revision + 1);
    this.immediate.next('session');
  }

  /**
   * Applies the state parsed from the URL at initialisation and publishes it
   * immediately (FR-012: "emits once at initialisation, after URL restore") —
   * so no consumer ever sees 300 ms of default scope on a deep link.
   *
   * A `clientCode` here is ignored for client roles by {@link setClientCode},
   * silently: it is a rule, not an error, so it raises no notice (FR-005,
   * filter-url validation table).
   */
  applyRestoredState(state: FilterState): void {
    this.writeState(state);
    this.immediate.next('restore');
  }

  private writeState(state: FilterState): void {
    this.setDateRange(state.dateRange);
    this.setClientCode(state.clientCode);
    this.appState.set(state.app);
    this.modelState.set(state.model);
    this.providerState.set(state.provider);
    this.appUnspecifiedState.set(state.appUnspecified);
    this.modelUnspecifiedState.set(state.modelUnspecified);
    this.providerUnspecifiedState.set(state.providerUnspecified);
    this.commitUser(state.user);
    this.setStatus(state.status);
  }

  /**
   * Drops a debounced publication that carries the scope already published.
   *
   * Two cases, both real:
   *
   * 1. `toObservable` replays the composed scope on subscription, which would
   *    otherwise surface as a spurious publication one window after bootstrap.
   * 2. The draft writes {@link reset} and {@link applyRestoredState} perform
   *    re-enter the stream behind their own immediate publication; without this
   *    they would debounce into a trailing duplicate, breaking obligation 4.
   *
   * A *user-visible* immediate publication is never dropped: `reset()` is one
   * user action and must be observable even from the default state (SC-004),
   * and initialisation must emit once whatever the URL restored (FR-012). The
   * `session` clear is immediate too — it must cancel a pending debounced
   * change — but it is nobody's action: when the published scope is already
   * the default it is dropped rather than triggering re-fetches against a
   * session that just ended (FR-019).
   *
   * `identity` is equality-checked for the same reason, and it is load-bearing
   * rather than incidental: on most first sign-ins the role change moves
   * nothing (an internal session with no selected client resolves to the same
   * `null`), and obligation 30 requires that transition to emit **nothing at
   * all**. Only a transition that genuinely moves the scope — a staff deep
   * link carrying `client=…` — publishes.
   */
  private shouldPublish(publication: Publication): boolean {
    if (publication.cause === 'reset' || publication.cause === 'restore') return true;
    // Either half is reason enough, and neither implies the other. A new
    // selection over an equivalent window (`today` → Custom covering today)
    // must reach the URL; an unchanged selection over a new window (the
    // UTC-midnight re-resolution, R-018) must reach consumers.
    return (
      !filterStateEquals(publication.state, this.lastPublishedState)
      || !filterScopeEquals(publication.scope, this.lastPublished)
    );
  }

  // ─── Observability (FR-018) ───────────────────────────────────────────────

  /**
   * Emitted on **published** changes only: draft mutations never reach
   * telemetry (data-model §3). A reset emits one `reset` event rather than
   * seven dimension events (obligation 18).
   */
  private recordTelemetry(publication: Publication): void {
    const previousState = this.lastPublishedState;
    const previousScope = this.lastPublished;
    this.lastPublishedState = publication.state;
    this.lastPublished = publication.scope;

    // Neither initialisation, nor the FR-019 session clear, nor the arrival of
    // the first owner is a user change: nobody moved a filter (FR-018).
    if (
      publication.cause === 'restore'
      || publication.cause === 'session'
      || publication.cause === 'identity'
    ) {
      return;
    }
    if (publication.cause === 'reset') {
      this.telemetry.reset();
      return;
    }

    // The WINDOW alone — `filterScopeEquals` also compares the other six
    // dimensions, so using it here would report a date-range change whenever
    // any dimension moved.
    const windowMoved =
      previousScope.range.fromUtc !== publication.scope.range.fromUtc
      || previousScope.range.toUtc !== publication.scope.range.toUtc;
    for (const [dimension, isCleared] of changedDimensions(
      previousState,
      publication.state,
      windowMoved,
    )) {
      this.telemetry.dimensionChanged(dimension, isCleared ? 'cleared' : 'set');
    }
  }
}

/**
 * The dimensions whose published value differs, in bar order, each paired with
 * whether the change *cleared* the dimension.
 *
 * The values themselves never leave this function: only the dimension token and
 * the change kind reach {@link FilterTelemetry}, which has no parameter capable
 * of carrying a value (FR-018, R-013). The date range is always `set` — it is
 * never absent (FR-002) — and an empty `status` array means "no constraint",
 * which is the cleared state.
 *
 * The date range counts as changed when EITHER its selection or its resolved
 * window moved, because neither implies the other: switching `today` to a
 * one-day Custom range covering today is a change the user made over an
 * unchanged window, while the UTC-midnight re-resolution (R-018) moves the
 * window under an unchanged selection. Comparing only one of the two
 * under-reports the other.
 */
function changedDimensions(
  previous: FilterState,
  next: FilterState,
  windowMoved: boolean,
): readonly (readonly [FilterDimension, boolean])[] {
  const changed: (readonly [FilterDimension, boolean])[] = [];

  if (windowMoved || !dateRangeSelectionEquals(previous.dateRange, next.dateRange)) {
    changed.push(['dateRange', false]);
  }
  if (previous.clientCode !== next.clientCode) {
    changed.push(['client', next.clientCode === null]);
  }
  // GH-164 C-4: an open-set dimension is a value OR the Unspecified choice, so both halves are
  // compared, and it is cleared only when neither is set — choosing Unspecified is a `set`.
  for (const dimension of ['app', 'model', 'provider'] as const) {
    const flag = `${dimension}Unspecified` as const;
    if (previous[dimension] !== next[dimension] || previous[flag] !== next[flag]) {
      changed.push([dimension, next[dimension] === null && !next[flag]]);
    }
  }
  if (previous.user !== next.user) changed.push(['user', next.user === null]);
  if (
    previous.status.length !== next.status.length
    || previous.status.some((value, index) => value !== next.status[index])
  ) {
    changed.push(['status', next.status.length === 0]);
  }

  return changed;
}
